import {ITenant, IUserProject} from 'app/models/user'
import {LOCAL_CURRENT_TENANT_ID, LOCAL_CURRENT_TENANT_NAME} from 'app/modules/auth'
import {IUserFE} from 'interfaces/IUserFE'

//Putting the functions here to decouple them from the CUser class so logic is not dependent upon data returned from the api

export function emHasProjectAccess(
  user: IUserFE,
  project_id: string,
  project: IUserProject,
  role?: string,
  feature?: string
) {
  if (user.is_global_admin) {
    // Global admins can do anything to anything with anything.
    return true
  }

  //passing in project because project and user are no longer coupled data
  //which means __role is not available per the user, cause user is now an interfce, but for now, it is not on the project
  //and the projet.users are CProjctUsers which have __role

  if (!project) {
    return false
  }

  let proj: IUserProject | undefined = undefined

  //can check that passed in project matches project_id passed in
  if (project.id === project_id) {
    proj = project
  }

  if (!proj) {
    return false
  }
  //check passed in role against the projct user
  const projectUser = project?.users?.find((f) => f.id === user.id)
  if (!projectUser) {
    return false
  }
  if (role === 'admin' && projectUser.__role !== 'admin') {
    return false
  }
  if (
    role === 'contributor' &&
    projectUser.__role !== 'admin' &&
    projectUser.__role !== 'contributor'
  ) {
    return false
  }
  if (
    role === 'viewer' &&
    projectUser.__role !== 'admin' &&
    projectUser.__role !== 'contributor' &&
    projectUser.__role !== 'viewer'
  ) {
    return false
  }
  if (
    feature &&
    proj.tenant_id &&
    user.features &&
    !emHasFeature(feature, proj.tenant_id, user.features)
  ) {
    return false
  }
  return true
}

export function emHasTenantAccess(
  user: IUserFE,
  tenant_id?: string,
  require_admin?: boolean,
  feature?: string
) {
  if (user.is_global_admin) {
    // Global admins can do anything to anything with anything.
    return true
  }
  const tenantID = tenant_id ?? emGetActiveTenantData(user)?.id ?? ''
  if (!tenant_id) {
    return false
  }

  if (feature && user.features && !emHasFeature(feature, tenantID, user.features)) {
    return false
  }

  let userChck = user
  if (user._subs_by_tenant_id === undefined) {
    userChck = emGetSubsByTenantId(user)
  }

  const subs = userChck && userChck._subs_by_tenant_id && userChck._subs_by_tenant_id[tenantID]
  if (Array.isArray(subs)) {
    for (const sub of subs) {
      if (sub.__is_tenant_admin || !require_admin) {
        return true
      }
    }
  }

  return false
}

export function emEditableBy(user: IUserFE, obj: any, project?: IUserProject) {
  if (!user) {
    return false
  }
  //remove global admin check, notes only editable by who wrote them for now to make sure thngs are working
  //note.created_by_id || user.id === note?.created?.by?.id)
  if (
    user.id === obj.created?.by ||
    user.id === obj.created_by_id ||
    user.id === obj.created?.by?.id
  ) {
    return true
  }
  if (project && obj.project_id) {
    return emHasProjectAccess(user, obj.project_id, project, 'admin')
  }
  if (obj.tenant_id) {
    return emHasTenantAccess(user, obj.tenant_id, true, 'note')
  }
  return false
}

export function emCreatableBy(
  user?: IUserFE,
  project_id?: string,
  tenant_id?: string,
  project?: IUserProject
) {
  if (!user) {
    return false
  }
  if (project_id && project) {
    return emHasProjectAccess(user, project_id, project, 'contributor')
  }
  return emHasTenantAccess(user, tenant_id, false, 'note')
}

export function emGetActiveTenantData(user: IUserFE) {
  return emTenantData(() => true, user, LOCAL_CURRENT_TENANT_ID, LOCAL_CURRENT_TENANT_NAME)
}

export function emTenantData(
  filter: (x: ITenant) => boolean,
  user: IUserFE,
  id_key?: string,
  name_key?: string
) {
  const id = id_key ? localStorage.getItem(id_key) ?? undefined : undefined
  const name = name_key ? localStorage.getItem(name_key) : undefined
  let checkUser = user
  if (user && !user._subs_by_tenant_id) {
    checkUser = emGetSubsByTenantId(user)
  }
  if (id !== undefined && checkUser._subs_by_tenant_id && id in checkUser._subs_by_tenant_id) {
    return name ? {id, name} : {id}
  }

  for (const id in checkUser._subs_by_tenant_id) {
    //default to the first tenant
    let tenant = checkUser._subs_by_tenant_id[id][0].tenant
    //check number of subscriptions to see if they have another tennt besides personal tenant
    let switched = false
    if ((checkUser.subscriptions?.length ?? 0) > 1) {
      //chek the profile
      if (checkUser?.profile?.subscription_preferences?.preferred_tenant_id) {
        const found = checkUser.subscriptions?.find(
          (f) => f.tenant?.id === checkUser.profile?.subscription_preferences?.preferred_tenant_id
        )
        if (found && found.tenant?.id) {
          tenant = checkUser._subs_by_tenant_id[found.tenant.id][0].tenant
          switched = true
        }
      }
      //setting to the second tenant, not the personal tenant
      if (!switched) {
        checkUser.subscriptions?.map((m) => {
          if (
            m.tenant?.id &&
            m.tenant.id !== checkUser.personal_tenant_id &&
            checkUser._subs_by_tenant_id
          ) {
            tenant = checkUser._subs_by_tenant_id[m.tenant.id][0].tenant
          }
        })
      }
    }

    if (tenant && tenant.id && filter(tenant)) {
      if (id_key && name_key) {
        localStorage.setItem(id_key, tenant.id)
        localStorage.setItem(name_key, tenant.name)
      }
      return {id: tenant.id, name: tenant.name}
    }
  }

  return undefined
}

export function emHasProjectFeature(
  tenant_id: string,
  userFeatures: {
    [k: string]: {
      [k: string]: string[]
    }
  }
) {
  return (
    emHasFeature('project-viewer', tenant_id, userFeatures) ||
    emHasFeature('project-contributor', tenant_id, userFeatures) ||
    emHasFeature('project-admin', tenant_id, userFeatures)
  )
}

export function emHasFeature(
  feature: string,
  tenantId: string,
  userFeatures: {
    [k: string]: {
      [k: string]: string[]
    }
  }
) {
  //TODO - can do this later
  // if (this.is_global_admin) {
  //   // Global admins can do anything to anything with anything.
  //   return true
  // }
  if (userFeatures === undefined) {
    throw Error('Cannot check features on a user other than yourself.')
  }
  if (userFeatures[feature] === undefined) {
    return false
  }
  const featureTenants = userFeatures[feature]

  const tenant_id = tenantId ?? localStorage.getItem(LOCAL_CURRENT_TENANT_ID)
  if (!tenant_id) {
    return true
  }
  return featureTenants[tenant_id] !== undefined
}

export function emGetSubsByTenantId(user: IUserFE) {
  const copyUser = {...user}
  copyUser._subs_by_tenant_id = {}
  for (const sub of user.subscriptions ?? []) {
    if (sub.tenant?.id) {
      if (!copyUser._subs_by_tenant_id[sub.tenant.id]) {
        copyUser._subs_by_tenant_id[sub.tenant.id] = []
      }
      copyUser._subs_by_tenant_id[sub.tenant.id].push(sub)
    }
  }
  return copyUser
}

export function emGetTenants(user: IUserFE) {
  const userWithSubsByTent = emGetSubsByTenantId(user)
  const output = []
  for (const tenant_id in userWithSubsByTent._subs_by_tenant_id) {
    const tenant = userWithSubsByTent._subs_by_tenant_id[tenant_id][0].tenant
    if (tenant) {
      output.push(tenant)
    }
  }
  return output
}

export function emGetPersonalTenantId(user: IUserFE) {
  const retval =
    user.subscriptions?.map((x) => x)?.find((f) => f?.tenant?.is_personal)?.tenant?.id ?? undefined
  return retval
}
